import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { setImpersonatedOrg, getImpersonatedOrg } from "@/lib/api";
import { LifeBuoy, X, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/** Persistent supportmodus-banner voor platform-superadmins.
 * Toont welke KDV momenteel ondersteund wordt, met snelknoppen om
 * te wisselen of de supportmodus af te sluiten. */
export default function SupportModeBanner() {
  const [orgId, setOrgId] = useState(() => getImpersonatedOrg());
  const [org, setOrg] = useState(null);
  const [isPlatform, setIsPlatform] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const me = await api.get("/platform/me");
        setIsPlatform(!!me.data.is_platform_super);
      } catch { setIsPlatform(false); }
    })();
  }, []);

  useEffect(() => {
    if (!orgId) { setOrg(null); return; }
    (async () => {
      try {
        // /tenant/me werkt binnen impersonatie en retourneert de actieve org.
        const r = await api.get("/tenant/me");
        setOrg({ organization: r.data, is_demo: r.data?.id === "demo_regenboog" });
      } catch { setOrg(null); }
    })();
  }, [orgId]);

  // Poll sessionStorage changes (bijv. na "Andere KDV selecteren").
  useEffect(() => {
    const onStorage = () => setOrgId(getImpersonatedOrg());
    window.addEventListener("storage", onStorage);
    const iv = setInterval(onStorage, 800);
    return () => { window.removeEventListener("storage", onStorage); clearInterval(iv); };
  }, []);

  if (!isPlatform || !orgId) return null;
  // Fallback tijdens korte transition: gebruik cache van vorige org-naam.
  const orgName = org?.organization?.name || (orgId === "org_default" ? "Kinderopvang Amsterdam" : orgId === "demo_regenboog" ? "Kinderopvang De Regenboog – Demo" : "Organisatie laden…");
  const isDemo = !!org?.is_demo || orgId === "demo_regenboog";

  const exitSupport = () => {
    setImpersonatedOrg(null);
    toast.success("Supportmodus afgesloten");
    // Hard-navigate zodat alle caches leeg zijn en KDV-shell verdwijnt.
    setTimeout(() => { window.location.href = "/platform/organisaties"; }, 150);
  };

  const switchOrg = () => {
    setImpersonatedOrg(null);
    setTimeout(() => { window.location.href = "/platform/organisaties"; }, 100);
  };

  return (
    <div
      className="w-full text-white border-b-2 sticky top-0 z-50 shadow-sm"
      style={{ background: "var(--sage)", borderColor: "rgba(255,255,255,0.35)" }}
      data-testid="support-mode-banner"
    >
      <div className="px-3 sm:px-4 lg:px-6 py-2 flex items-center gap-3 flex-wrap">
        <LifeBuoy className="h-4 w-4 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold leading-tight" data-testid="support-mode-title">
            SUPPORTMODUS — Je ondersteunt momenteel <span className="underline decoration-white/80 decoration-2 underline-offset-2">{orgName}</span>
          </div>
          <div className="text-[11px] text-white/80 leading-tight mt-0.5" data-testid="support-mode-meta">
            {isDemo ? "Demo-omgeving" : "Productie-omgeving"} · org_id: <span className="font-mono">{orgId}</span>
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button
            size="sm"
            variant="outline"
            onClick={switchOrg}
            className="border-white/40 bg-white/10 text-white hover:bg-white/20 h-8"
            data-testid="support-switch-btn"
          >
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Andere KDV selecteren
          </Button>
          <Button
            size="sm"
            onClick={exitSupport}
            className="bg-white hover:bg-neutral-50 h-8"
            style={{ color: "var(--sage)" }}
            data-testid="support-exit-btn"
          >
            <X className="h-3.5 w-3.5 mr-1" /> Supportmodus afsluiten
          </Button>
        </div>
      </div>
    </div>
  );
}
